/**
 * PATH: apps/qa/src/pages/reports/ShareReportDialog.tsx
 * PURPOSE: Modal dialog for sharing QA reports via portal link
 * WHY: Extracted from ReportsPage.tsx to keep files under 300 lines
 */

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { reportApi } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import type { Report } from '@/types/qa'
import {
  X,
  Clock,
  Loader2,
  Copy,
  Check,
  Link2,
} from 'lucide-react'

interface ShareDialogProps {
  report: Report
  isOpen: boolean
  onClose: () => void
}

export function ShareReportDialog({ report, isOpen, onClose }: ShareDialogProps) {
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [expiresInHours, setExpiresInHours] = useState(168) // 1 week
  const [recipientEmail, setRecipientEmail] = useState('')
  const [permissions, setPermissions] = useState<Array<'review' | 'comment' | 'sign' | 'download'>>(['review', 'download'])
  
  const shareMutation = useMutation({
    mutationFn: async () => {
      const response = await reportApi.shareReport(report.id, {
        permissions,
        recipient_email: recipientEmail || undefined,
        expires_in_hours: expiresInHours,
      })
      return response
    },
    onSuccess: (data) => {
      if (data.portal_url) {
        setShareUrl(data.portal_url)
      }
    },
  })
  
  const handleCopy = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }
  
  if (!isOpen) return null
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div role="dialog" aria-modal="true" aria-labelledby="share-report-title" className="bg-card rounded-xl p-6 w-full max-w-md shadow-xl border border-border animate-fadeIn">
        <div className="flex items-center justify-between mb-4">
          <h3 id="share-report-title" className="text-lg font-semibold text-foreground">Share issued revision</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-3 rounded-lg bg-muted/50 mb-4">
          <p className="font-medium text-foreground">{report?.report_name}</p>
          <p className="text-sm text-muted-foreground mt-0.5">
            Generated {formatDate(report?.generated_at)}
          </p>
        </div>
        
        {shareUrl ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="flex-1 px-3 py-2 rounded-lg border border-input bg-muted text-foreground text-sm"
              />
              <button
                onClick={handleCopy}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary-600 transition-colors"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              <Clock className="w-3 h-3 inline mr-1" />
              This link expires in {expiresInHours / 24} days
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Link Expiry
              </label>
              <select
                value={expiresInHours}
                onChange={(e) => setExpiresInHours(Number(e.target.value))}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value={24}>1 day</option>
                <option value={72}>3 days</option>
                <option value={168}>1 week</option>
                <option value={720}>30 days</option>
              </select>
            </div>
            <div><label className="block text-sm font-medium text-foreground mb-1.5">Recipient email (optional)</label><input type="email" value={recipientEmail} onChange={(event) => setRecipientEmail(event.target.value)} className="w-full rounded-lg border border-input bg-background px-3 py-2 text-foreground" /></div>
            <fieldset><legend className="mb-2 text-sm font-medium text-foreground">Portal permissions</legend><div className="grid grid-cols-2 gap-2">{(['review', 'comment', 'sign', 'download'] as const).map((permission) => <label key={permission} className="flex items-center gap-2 text-sm capitalize"><input type="checkbox" checked={permissions.includes(permission)} onChange={(event) => setPermissions((current) => event.target.checked ? [...current, permission] : current.filter((value) => value !== permission))} />{permission}</label>)}</div></fieldset>
            
            <button
              onClick={() => shareMutation.mutate()}
              disabled={shareMutation.isPending || permissions.length === 0}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 transition-colors disabled:opacity-50"
            >
              {shareMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating link...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4" />
                  Create Share Link
                </>
              )}
            </button>
          </div>
        )}
        {shareMutation.isError && <p role="alert" className="mt-3 text-sm text-destructive">The share link could not be created. Check report sharing permission and try again.</p>}
        
        <div className="mt-4 pt-4 border-t border-border">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 rounded-xl border border-input text-foreground font-medium hover:bg-accent transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
